import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useTerminology } from '@/hooks/useTerminology';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { format, addMonths } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon } from 'lucide-react';
import { useFormatting } from '@/hooks/useFormatting';

interface CostReductionWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: (eventData: any) => void;
}

export function CostReductionWizard({ open, onOpenChange, onComplete }: CostReductionWizardProps) {
  const { formatCurrency } = useFormatting();
  const { getScenarioTerm } = useTerminology();
  const [step, setStep] = useState(1);
  
  // Step 1: Category Selection
  const [category, setCategory] = useState<'salaries' | 'rent' | 'software' | 'marketing' | 'travel' | 'supplies'>('salaries');

  // Step 2: Reduction Details
  const [currentCost, setCurrentCost] = useState('50000');
  const [reductionType, setReductionType] = useState<'amount' | 'percentage'>('percentage');
  const [reductionAmount, setReductionAmount] = useState('10000');
  const [reductionPercentage, setReductionPercentage] = useState('20');

  // Step 3: Implementation
  const [timing, setTiming] = useState<'immediate' | 'phased'>('immediate');
  const [phaseMonths, setPhaseMonths] = useState('3');
  const [includeOneTimeCosts, setIncludeOneTimeCosts] = useState(false);
  const [oneTimeCostDescription, setOneTimeCostDescription] = useState('');
  const [oneTimeCostAmount, setOneTimeCostAmount] = useState('5000');

  // Step 4: Review
  const [description, setDescription] = useState('');

  const handleNext = () => setStep(step + 1);
  const handleBack = () => setStep(step - 1);

  const handleComplete = () => {
    const eventData = {
      type: 'cost_reduction',
      category,
      currentCost: parseFloat(currentCost),
      reductionType,
      reductionAmount: reductionType === 'amount' ? parseFloat(reductionAmount) : (parseFloat(currentCost) * parseFloat(reductionPercentage)) / 100,
      reductionPercentage: reductionType === 'percentage' ? parseFloat(reductionPercentage) : 0,
      timing,
      phaseMonths: timing === 'phased' ? parseInt(phaseMonths) : 0,
      includeOneTimeCosts,
      oneTimeCostDescription: includeOneTimeCosts ? oneTimeCostDescription : '',
      oneTimeCostAmount: includeOneTimeCosts ? parseFloat(oneTimeCostAmount) : 0,
      description,
    };
    onComplete(eventData);
    onOpenChange(false);
    resetForm();
  };

  const resetForm = () => {
    setStep(1);
    setCategory('salaries');
    setCurrentCost('50000');
    setReductionType('percentage');
    setReductionAmount('10000');
    setReductionPercentage('20');
    setTiming('immediate');
    setPhaseMonths('3');
    setIncludeOneTimeCosts(false);
    setOneTimeCostDescription('');
    setOneTimeCostAmount('5000');
    setDescription('');
  };

  const getCategoryLabel = () => {
    const labels = {
      salaries: 'Salaries',
      rent: 'Rent',
      software: 'Software/Subscriptions',
      marketing: 'Marketing',
      travel: 'Travel',
      supplies: 'Supplies',
    };
    return labels[category];
  };

  const calculateReduction = () => {
    if (reductionType === 'amount') {
      return parseFloat(reductionAmount) || 0;
    } else {
      return ((parseFloat(currentCost) || 0) * (parseFloat(reductionPercentage) || 0)) / 100;
    }
  };

  const calculateNewCost = () => {
    return (parseFloat(currentCost) || 0) - calculateReduction();
  };

  const calculate12MonthImpact = () => {
    const monthlySavings = calculateReduction();
    const oneTimeCost = includeOneTimeCosts ? (parseFloat(oneTimeCostAmount) || 0) : 0;
    const totalSavings = monthlySavings * 12;
    const netImpact = totalSavings - oneTimeCost;
    
    return { monthlySavings, oneTimeCost, totalSavings, netImpact };
  };

  const calculateBreakEven = () => {
    if (!includeOneTimeCosts) return 0;
    const monthlySavings = calculateReduction();
    const oneTimeCost = parseFloat(oneTimeCostAmount) || 0;
    if (monthlySavings <= 0) return null;
    return Math.ceil(oneTimeCost / monthlySavings);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Cost Reduction Program - Step {step} of 4</DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <h3 className="font-semibold">Select Cost Category</h3>

            <div className="space-y-2">
              <Label>Category to Reduce *</Label>
              <Select value={category} onValueChange={(val) => setCategory(val as any)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="salaries">Salaries</SelectItem>
                  <SelectItem value="rent">Rent</SelectItem>
                  <SelectItem value="software">Software/Subscriptions</SelectItem>
                  <SelectItem value="marketing">Marketing</SelectItem>
                  <SelectItem value="travel">Travel</SelectItem>
                  <SelectItem value="supplies">Supplies</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={handleNext}>Next: Reduction Details →</Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <h3 className="font-semibold">Reduction Details</h3>

            <div className="space-y-2">
              <Label htmlFor="currentCost">Current Monthly Cost (for reference) *</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm">£</span>
                <Input
                  id="currentCost"
                  type="number"
                  value={currentCost}
                  onChange={(e) => setCurrentCost(e.target.value)}
                />
                <span className="text-sm text-muted-foreground">per month</span>
              </div>
            </div>

            <div className="space-y-4 mt-4">
              <Label>Reduction Method</Label>
              
              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  id="percentage"
                  checked={reductionType === 'percentage'}
                  onChange={() => setReductionType('percentage')}
                />
                <Label htmlFor="percentage" className="flex-1">
                  <span className="font-semibold">Percentage Reduction</span>
                </Label>
              </div>
              
              {reductionType === 'percentage' && (
                <div className="ml-6 flex items-center gap-2">
                  <Input
                    type="number"
                    value={reductionPercentage}
                    onChange={(e) => setReductionPercentage(e.target.value)}
                    className="w-24"
                  />
                  <span className="text-sm">% reduction</span>
                </div>
              )}

              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  id="amount"
                  checked={reductionType === 'amount'}
                  onChange={() => setReductionType('amount')}
                />
                <Label htmlFor="amount" className="flex-1">
                  <span className="font-semibold">Fixed Amount Reduction</span>
                </Label>
              </div>

              {reductionType === 'amount' && (
                <div className="ml-6 flex items-center gap-2">
                  <span className="text-sm">£</span>
                  <Input
                    type="number"
                    value={reductionAmount}
                    onChange={(e) => setReductionAmount(e.target.value)}
                    className="w-32"
                  />
                  <span className="text-sm text-muted-foreground">per month</span>
                </div>
              )}
            </div>

            <div className="bg-muted p-4 rounded-lg space-y-2">
              <p className="font-semibold text-sm">Reduction Summary:</p>
              <p className="text-sm">Current cost: {formatCurrency(parseFloat(currentCost))}/month</p>
              <p className="text-sm text-green-600">Reduction: -{formatCurrency(calculateReduction())}/month</p>
              <p className="text-sm font-semibold">New monthly cost: {formatCurrency(calculateNewCost())}/month</p>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={handleBack}>← Back</Button>
              <Button onClick={handleNext} disabled={!currentCost}>Next: Implementation →</Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <h3 className="font-semibold">Implementation Timeline</h3>

            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  id="immediate"
                  checked={timing === 'immediate'}
                  onChange={() => setTiming('immediate')}
                />
                <Label htmlFor="immediate">
                  <span className="font-semibold">Immediate</span>
                  <p className="text-sm text-muted-foreground">Full reduction starts next month</p>
                </Label>
              </div>

              <div className="flex items-start gap-2">
                <input
                  type="radio"
                  id="phased"
                  checked={timing === 'phased'}
                  onChange={() => setTiming('phased')}
                  className="mt-1"
                />
                <div className="flex-1">
                  <Label htmlFor="phased">
                    <span className="font-semibold">Phased Implementation</span>
                    <p className="text-sm text-muted-foreground">Gradual reduction over time</p>
                  </Label>
                  {timing === 'phased' && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-sm">Phase over</span>
                      <Input
                        type="number"
                        value={phaseMonths}
                        onChange={(e) => setPhaseMonths(e.target.value)}
                        className="w-20"
                      />
                      <span className="text-sm">months</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {timing === 'phased' && (
              <div className="bg-muted p-4 rounded-lg space-y-2">
                <p className="font-semibold text-sm">Phased Implementation:</p>
                <p className="text-sm">
                  Reduction per month: {formatCurrency(calculateReduction() / parseInt(phaseMonths), 'full', {maximumFractionDigits: 0})}
                </p>
                <p className="text-sm">
                  Full savings achieved: Month {phaseMonths}
                </p>
              </div>
            )}

            <div className="border-t pt-4 space-y-4">
              <h4 className="font-semibold">One-Time Implementation Costs</h4>
              
              <div className="flex items-start gap-2">
                <Checkbox
                  id="oneTimeCosts"
                  checked={includeOneTimeCosts}
                  onCheckedChange={(checked) => setIncludeOneTimeCosts(checked as boolean)}
                />
                <div className="flex-1 space-y-2">
                  <Label htmlFor="oneTimeCosts">Include one-time costs (e.g., redundancy, exit fees)</Label>
                  {includeOneTimeCosts && (
                    <>
                      <Input
                        placeholder="e.g., Redundancy payments, contract exit fees"
                        value={oneTimeCostDescription}
                        onChange={(e) => setOneTimeCostDescription(e.target.value)}
                      />
                      <div className="flex items-center gap-2">
                        <span className="text-sm">£</span>
                        <Input
                          type="number"
                          value={oneTimeCostAmount}
                          onChange={(e) => setOneTimeCostAmount(e.target.value)}
                          className="w-32"
                        />
                        <span className="text-sm text-muted-foreground">one-time cost</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={handleBack}>← Back</Button>
              <Button onClick={handleNext}>Next: Review →</Button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            <h3 className="font-semibold">Review Event Summary</h3>

            <div className="space-y-2">
              <p className="font-semibold">Cost Reduction: {getCategoryLabel()}</p>
              <p className="text-sm">Current cost: {formatCurrency(parseFloat(currentCost))}/month</p>
              <p className="text-sm">Reduction: {formatCurrency(calculateReduction())}/month ({reductionType === 'percentage' ? `${reductionPercentage}%` : 'fixed amount'})</p>
              <p className="text-sm">New cost: {formatCurrency(calculateNewCost())}/month</p>
              <p className="text-sm">Implementation: {timing === 'immediate' ? 'Immediate' : `Phased over ${phaseMonths} months`}</p>
            </div>

            <div className="border-t pt-4 space-y-3">
              <p className="font-semibold">─── CASH FLOW IMPACT ───────────────────────────</p>

              {includeOneTimeCosts && (
                <>
                  <p className="font-semibold text-sm">One-Time Costs:</p>
                  <p className="text-sm text-red-600">• {oneTimeCostDescription || 'Implementation costs'}: -{formatCurrency(parseFloat(oneTimeCostAmount))}</p>
                </>
              )}

              <p className="font-semibold text-sm mt-4">Monthly Savings:</p>
              <p className="text-sm text-green-600">• {getCategoryLabel()} reduction: +{formatCurrency(calculateReduction())}/month</p>

              <p className="font-semibold text-sm mt-4">─── NET IMPACT (12 months) ──────────────────────</p>
              <p className="text-sm">Total savings: +{formatCurrency(calculate12MonthImpact().totalSavings)}</p>
              {includeOneTimeCosts && (
                <p className="text-sm">One-time costs: -{formatCurrency(calculate12MonthImpact().oneTimeCost)}</p>
              )}
              <p className="text-sm font-semibold">
                Net 12-month impact: +{formatCurrency(calculate12MonthImpact().netImpact)}
              </p>

              {calculateBreakEven() && calculateBreakEven()! <= 12 && (
                <p className="text-sm">
                  Break-even: Month {calculateBreakEven()} (implementation costs recovered)
                </p>
              )}

              {calculate12MonthImpact().netImpact > 0 && (
                <div className="bg-green-50 border border-green-200 rounded p-3 mt-4">
                  <p className="text-sm text-green-800">
                    ✓ This cost reduction will improve cash flow by {formatCurrency(calculate12MonthImpact().netImpact)} over 12 months
                  </p>
                </div>
              )}

              {includeOneTimeCosts && calculateBreakEven() && calculateBreakEven()! > 12 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mt-4">
                  <p className="text-sm text-yellow-800">
                    ⚠️ Break-even will occur in month {calculateBreakEven()} (beyond 12-month horizon)
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-2 pt-4">
              <Label htmlFor="description">Event Description (optional):</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g., Reduce office space and move to hybrid working"
                rows={2}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={handleBack}>← Back</Button>
              <Button onClick={handleComplete}>Add Event to {getScenarioTerm(true)}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
